import { EnderecoService } from '@/Services/EnderecoService';
import Ordenar from '@/Utils/Ordenacao';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input, Modal, notification } from 'antd';
import { useState } from 'react';
import InputMask from 'react-input-mask';
import axios from 'axios';
import { PropriedadesDeNotificacao } from '@/Utils/Notificacoes';
import { EnumTelefoneTipo } from '@/Models/Pessoa/Contato/Telefone';
import { EnumEnderecoTipo, TypeEndereco } from '@/Models/Pessoa/Endereco';
import dayjs from 'dayjs';
import { TypePessoa } from '@/Models/Pessoa/Pessoa';

export interface IInputCnpjProps {
    initialValue ?: string;
    onChangePessoa ?: (pessoa:TypePessoa) => void;
    onBlur?:(e:any)=>void;
    onErroAoBuscarDados?:()=>void;
}

export default function InputCnpj({
    initialValue,
    onChangePessoa,
    onErroAoBuscarDados,
    onBlur,
    ...props
}:IInputCnpjProps) {
    const enderecoService = new EnderecoService();

    const [ cnpjNovo, setCnpjNovo ] = useState('');
    const [ openConfirmBuscaCnpj, setOpenConfirmBuscaCnpj ] = useState(false);

    const handleCancelPopconfirmBuscarCnpj = () => {
        setOpenConfirmBuscaCnpj(false);
    }

    const estadosDisponiveis = useQuery({
        queryKey: ["listarEstados"],
        queryFn: () => enderecoService.listarEstados().then((r) => {
            r.data.sort((a, b) => Ordenar.String(a.nome, b.nome))
            return r;
        }),
        staleTime : 60000
    });

    const {
        mutate : mutateSetMunicipio
    } = useMutation({
        mutationFn : ({ codEstado }:{ codEstado:number; codIbgeMunicipio : number; pessoa:TypePessoa  }) => enderecoService.listarMunicipios({ codigo: codEstado }),
        onSettled(data, error, variables, context) {
            const municipio = data?.data?.find(f => f.codMunicipio === variables.codIbgeMunicipio);
            if (variables.pessoa.enderecos !== undefined && (variables.pessoa.enderecos?.length ?? 0) > 0 && municipio !== undefined)
            {
                variables.pessoa.enderecos[0].municipio = municipio;
                if (municipio.estado !== undefined)
                    variables.pessoa.enderecos[0].estado = municipio.estado;
            }
            onChangePessoa?.(variables.pessoa);
        },
    });

    const {
        mutate : buscarCnpj,
        isPending : isPendingBuscaCnpjData
    } = useMutation({
        mutationFn : (cnpj:string) => {
            const cnpjFormatado = cnpj.replace(/[^a-zA-Z0-9]/g, '');
            return axios.get<EmpresaCnpj>(`https://brasilapi.com.br/api/cnpj/v1/${cnpjFormatado}`);
        },
        onSuccess(data, variables, context) {
            const cnpjFormatado = variables.replace(/[^a-zA-Z0-9]/g, '');
            const cnpjNovo = data.data?.cnpj?.replace(/[^a-zA-Z0-9]/g, '');

            if (cnpjNovo === cnpjFormatado)
            {
                const pessoaApi = data.data;
                const dataRegistro = dayjs(pessoaApi.data_inicio_atividade, "YYYY-MM-DD");
                const estadoSelecao = estadosDisponiveis?.data?.data?.find(f => f.sigla == pessoaApi.uf);

                const pessoa = {
                    nome : pessoaApi.razao_social,
                    nomeSocial : (pessoaApi.nome_fantasia ?? '').trim() === '' ? pessoaApi.razao_social : pessoaApi.nome_fantasia,
                    dataRegistro : dataRegistro,
                    descricaoMaterialServicoPrestado : pessoaApi.cnae_fiscal_descricao,
                    cnpj : cnpjNovo,
                    enderecos : pessoaApi.cep && estadoSelecao ? [
                        {
                            ativo : true,
                            tipo : {
                                codigo : EnumEnderecoTipo.COMERCIAL,
                                tipo : "Comércial"
                            },
                            cep : pessoaApi.cep.toString(),
                            logradouro : pessoaApi.logradouro,
                            complemento : pessoaApi.complemento,
                            bairro : pessoaApi.bairro,
                            numero : pessoaApi.numero,
                            estado : estadoSelecao,
                            municipio : { 
                                codMunicipio : pessoaApi.codigo_municipio_ibge,
                                nome : pessoaApi.municipio
                            },
                        } as TypeEndereco
                    ] : [],
                    telefones : [
                        {
                            ativo : true,
                            principal : true,
                            tipo : {
                                codigo : EnumTelefoneTipo.COMERCIAL,
                                tipo : "Comércial"
                            },
                            nomeContato : "Principal",
                            ddd : pessoaApi.ddd_telefone_1.substring(0, 2),
                            numero : pessoaApi.ddd_telefone_1.substring(2)
                        }
                    ]
                } as TypePessoa;

                if (estadoSelecao)
                    mutateSetMunicipio({
                        codEstado : estadoSelecao.codigo as number,
                        codIbgeMunicipio : pessoaApi.codigo_municipio_ibge,
                        pessoa : pessoa
                    });
                else
                    onChangePessoa?.(pessoa);
            }
            else
            {
                onErroAoBuscarDados?.();
                notification.open({
                    ...PropriedadesDeNotificacao.Aviso('Não foi possível identificar as informações do cnpj informado.')
                });
            }
        },
        onError(error, variables, context) {
            onErroAoBuscarDados?.();
            notification.open({
                ...PropriedadesDeNotificacao.Erro(`Ocorreu um erro inesperado durante a consulta do CNPJ ${variables}`)
            });
        },
        onSettled(data, error, variables, context) {
            handleCancelPopconfirmBuscarCnpj();
        },
    });

    initialValue = initialValue ?? '';
    return <>
        <Modal
            title="Carregar dados empresa"
            open={openConfirmBuscaCnpj}
            onOk={() => buscarCnpj(cnpjNovo)}
            okButtonProps={{ loading: isPendingBuscaCnpjData }}
            onCancel={handleCancelPopconfirmBuscarCnpj}
        >
            Identificamos que ocorreu uma mudança no CNPJ informado, gostaria de forçar a atualizanção das informações cadastrais?
        </Modal>
        <InputMask 
            mask="99.999.999/9999-99"
            onBlur={(evento) => {
                const value = evento.target.value.replace(/[^a-zA-Z0-9]/g, '');
                if (value.length === 14)
                {
                    setCnpjNovo(value);
                    if (initialValue.trim() === '')
                        buscarCnpj(value);
                    else if (initialValue.trim().replace(/[^a-zA-Z0-9]/g, '') !== value)
                    {
                        setOpenConfirmBuscaCnpj(true);
                    }
                }
                onBlur?.(evento);
            }}
            {...props}
        >
            <Input />
        </InputMask>
    </>;
}



/**
 * Quadro de Sócios e Administradores (QSA)
 */
export interface Socio {
  /** Nome do país onde se localiza o sócio */
  pais: string | null;
  /** Nome do sócio pessoa física ou a razão social e/ou nome empresarial da pessoa jurídica e/ou nome do sócio/razão social do sócio estrangeiro */
  nome_socio: string;
  /** Código do país onde se localiza o sócio */
  codigo_pais: number | null;
  /** Faixa etária do sócio */
  faixa_etaria: string;
  /** CPF ou CNPJ do sócio (sócio estrangeiro não tem esta informação) */
  cnpj_cpf_do_socio: string;
  /** Qualificação do sócio */
  qualificacao_socio: string;
  /** Código da faixa etária do sócio */
  codigo_faixa_etaria: number;
  /** Data de entrada na sociedade (Formato de data) */
  data_entrada_sociedade: string;
  /** Identificador do sócio. Valores: 1 (PESSOA JURÍDICA), 2 (PESSOA FÍSICA), 3 (ESTRANGEIRO) */
  identificador_de_socio: 1 | 2 | 3;
  cpf_representante_legal: string | null;
  nome_representante_legal: string | null;
  codigo_qualificacao_socio: number;
  /** Qualificação do representante legal */
  qualificacao_representante_legal: string;
  /** Código da qualificação do representante legal */
  codigo_qualificacao_representante_legal: number;
}

/**
 * Atividades Econômicas Secundárias (CNAE)
 */
export interface CnaeSecundario {
  /** Código da Atividade Econômica SECUNDÁRIA do estabelecimento */
  codigo: number;
  /** Descrição da Atividade Econômica SECUNDÁRIA do estabelecimento */
  descricao: string;
}

/**
 * Regime Tributário
 */
export interface RegimeTributario {
  /** Ano de referência do regime tributário */
  ano: number | null;
  /** CNPJ da SCP */
  cnpj_da_scp: string | null;
  /** Forma de tributação da empresa */
  forma_de_tributacao: string;
  /** Quantidade de escrituração da empresa */
  quantidade_de_escrituracoes: number;
  /** Situação especial */
  situacao_especial: string | null;
}

/**
 * Interface Principal do Estabelecimento / Empresa (CNPJ)
 */
export interface EmpresaCnpj {
  /** Sigla da unidade da federação. Quando localizado no exterior, será 'EX' */
  uf: string;
  /** Código de endereçamento postal referente ao logradouro */
  cep: number;
  /** Quadro de sócios e administradores */
  qsa: Socio[];
  /** Número Cadastro Nacional da Pessoa Jurídica */
  cnpj: string;
  /** Nome do país onde se localiza o estabelecimento */
  pais: string | null;
  /** E-mail do contribuinte */
  email: string | null;
  /** Descrição do porte da empresa */
  porte: "NÃO INFORMADO" | "MICRO EMPRESA" | "EMPRESA DE PEQUENO PORTE" | "DEMAIS" | null;
  /** Bairro onde se localiza o estabelecimento */
  bairro: string;
  /** Número onde se localiza o estabelecimento. Quando não houver, haverá 'S/N' */
  numero: string;
  /** Contém o ddd e o número do fax (Formato: ^[0-9]{2}[0-9]{8,9}$) */
  ddd_fax: string | null;
  /** Nome do município de jurisdição onde se encontra o estabelecimento */
  municipio: string | null;
  /** Nome do logradouro onde se localiza o estabelecimento */
  logradouro: string;
  /** Código da Atividade Econômica PRINCIPAL do estabelecimento */
  cnae_fiscal: number;
  /** Código do país onde se localiza o estabelecimento */
  codigo_pais: number | null;
  /** Complemento do endereço de localização do estabelecimento */
  complemento: string;
  /** Código do porte da empresa. Valores: 0 (NÃO INFORMADO), 1 (MICRO EMPRESA), 3 (EMPRESA DE PEQUENO PORTE), 5 (DEMAIS) */
  codigo_porte: 0 | 1 | 3 | 5;
  /** Nome empresarial da pessoa jurídica */
  razao_social: string;
  /** Corresponde ao nome fantasia */
  nome_fantasia: string;
  /** Capital social da empresa */
  capital_social: number;
  /** Contém o ddd e o número do telefone 1 (Formato: ^[0-9]{2}[0-9]{8,9}$) */
  ddd_telefone_1: string;
  /** Contém o ddd e o número do telefone 2 (Formato: ^[0-9]{2}[0-9]{8,9}$) */
  ddd_telefone_2: string | null;
  /** Indicador da existência da opção pelo MEI. true (SIM), false (NÃO), null (OUTROS) */
  opcao_pelo_mei: boolean | null;
  /** Código do município de jurisdição onde se encontra o estabelecimento */
  codigo_municipio: number | null;
  /** Array de atividades econômicas secundárias */
  cnaes_secundarios: CnaeSecundario[];
  /** Natureza jurídica da empresa */
  natureza_juridica: string;
  /** Array de regime tributário ou nulo */
  regime_tributario: RegimeTributario[] | null;
  /** Indicador da existência da opção pelo Simples. true (SIM), false (NÃO), null (OUTROS) */
  opcao_pelo_simples: boolean | null;
  /** Identifica a situação cadastral. Valores: 1 (NULA), 2 (ATIVA), 3 (SUSPENSA), 4 (INAPTA), 8 (BAIXADA) */
  situacao_cadastral: 1 | 2 | 3 | 4 | 8;
  /** Data de opção pelo MEI (Formato de data) */
  data_opcao_pelo_mei: string | null;
  /** Data de exclusão do MEI (Formato de data) */
  data_exclusao_do_mei: string | null;
  /** Descrição da Atividade Econômica PRINCIPAL do estabelecimento */
  cnae_fiscal_descricao: string;
  /** Código do IBGE do município de jurisdição onde se encontra o estabelecimento */
  codigo_municipio_ibge: number;
  /** Data de início das atividades (Formato de data) */
  data_inicio_atividade: string;
  /** Data em que a empresa entrou em situação especial (Formato de data) */
  data_situacao_especial: string | null;
  /** Data da opção pelo Simples (Formato de data) */
  data_opcao_pelo_simples: string | null;
  /** Data da situação cadastral (Formato de data) */
  data_situacao_cadastral: string;
  /** Nome da cidade no exterior */
  nome_cidade_exterior: string | null;
  /** Código da natureza jurídica */
  codigo_natureza_juridica: number;
  /** Data da exclusão do Simples (Formato de data) */
  data_exclusao_do_simples: string | null;
  /** Código do motivo da situação cadastral */
  motivo_situacao_cadastral: number;
  /** Preenchido para os casos de órgãos e entidades do grupo de natureza jurídica 1XXX */
  ente_federativo_responsavel: string;
  /** Identifica se o CNPJ pertence a uma matriz ou filial. Valores: 1 (MATRIZ), 2 (FILIAL) */
  identificador_matriz_filial: 1 | 2;
  /** Código de qualificação da pessoa física responsável pela empresa */
  qualificacao_do_responsavel: number;
  /** Descrição textual da situação cadastral da empresa */
  descricao_situacao_cadastral: "NULA" | "ATIVA" | "SUSPENSA" | "INAPTA" | "BAIXADA";
  /** Descrição do tipo de logradouro */
  descricao_tipo_de_logradouro: string;
  /** Descrição do motivo da situação cadastral */
  descricao_motivo_situacao_cadastral: string;
  /** Descrição textual do identificador de matriz ou filial */
  descricao_matriz_filial: "MATRIZ" | "FILIAL";
}